import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import { cx } from "./ui";

/**
 * Map the visibility state to the input's `type`. Kept as a pure function so
 * the masked/revealed contract is unit-testable without a DOM: the password is
 * always hidden until the user asks to reveal it, and revealing never touches
 * the value itself — only the input type.
 */
export function passwordInputType(visible: boolean): "text" | "password" {
  return visible ? "text" : "password";
}

const INPUT_CLASS =
  "w-full rounded-md border-0 px-3 py-2 pr-10 text-sm text-slate-900 ring-1 ring-inset placeholder:text-slate-400 focus:ring-2 focus:ring-inset";

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string | undefined;
  required?: boolean | undefined;
  /** Layout classes for the positioning wrapper (e.g. `mt-1`). */
  className?: string | undefined;
  /** Draw the field with the error treatment while preserving its own styling. */
  invalid?: boolean | undefined;
  "aria-invalid"?: boolean | undefined;
  "aria-describedby"?: string | undefined;
  placeholder?: string | undefined;
}

/**
 * A password field with an eye / eye-off affordance.
 *
 * The toggle is a real `<button type="button">` inside the input's padding, so
 * it is reachable by keyboard (Tab then Enter/Space) and never submits the form.
 * Its `aria-label` changes with the state, and the value is untouched by a
 * toggle — this component never reads or logs the password beyond passing it to
 * `onChange`. The `pr-10` padding reserves room so the text never runs under the
 * icon, on desktop or mobile.
 */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  required,
  className,
  invalid = false,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  placeholder,
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className={cx("relative", className)}>
      <input
        id={id}
        name={id}
        type={passwordInputType(visible)}
        autoComplete={autoComplete}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={cx(
          INPUT_CLASS,
          invalid
            ? "ring-rose-500 focus:ring-rose-500"
            : "ring-slate-300 focus:ring-slate-900",
        )}
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        title={visible ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:text-slate-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-900"
      >
        {visible ? (
          <EyeOff className="size-4" aria-hidden="true" />
        ) : (
          <Eye className="size-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
